import camelCase from 'lodash/camelCase';
import type { PiniaPlugin, Store } from 'pinia';
import type { Socket } from 'socket.io-client';

declare module 'pinia' {
    export interface PiniaCustomProperties {
        $socket: {
            emit: (event: string, ...args: unknown[]) => void;
        };
    }
}

// Pinia equivalent of vuex-socketio (src/vuex/plugins/websocket.util.js).
// Maps socket events to store actions by convention: socket event 'some-event'
// calls store action 'socketOn_someEvent' on any store that defines it.
// Outbound events: use store.$socket.emit(event, payload) — the vuex-socketio
// 'socketEmit_' action prefix is not carried forward; use $socket.emit() directly.
export function createSocketIoPlugin (socket: Socket): PiniaPlugin {
    // stores accumulates every registered Pinia store for the lifetime of the app.
    // All stores in this codebase are application-scoped (not route-scoped), so
    // none are disposed mid-session. Revisit if route-scoped stores are introduced.
    const stores: Store[] = [];
    let listenerRegistered = false;

    return ({ store }) => {
        stores.push(store);

        if (!listenerRegistered) {
            listenerRegistered = true;
            socket.onAny((event: string, ...args: unknown[]) => {
                const actionName = `socketOn_${camelCase(event)}`;

                for (const s of stores) {
                    const actions = s as unknown as Record<string, (...a: unknown[]) => void>;

                    if (typeof actions[actionName] === 'function') {
                        actions[actionName](...args);
                    }
                }
            });
        }

        return {
            $socket: {
                emit: (event: string, ...args: unknown[]) => socket.emit(event, ...args),
            },
        };
    };
}
